"use client";

import { NumericFormat } from "react-number-format";
import { Slider, TextField } from "@mui/material";

interface ValueSliderProps {
  filterValueToSliderValue: (fallbackValue: number, value?: number) => number;
  label: string;
  max: number;
  maxPlaceholder?: string;
  minPlaceholder?: string;
  onChange: (value: [number, number]) => void;
  prefix?: string;
  sliderValueToFilterValue: (value: number) => number;
  value: [number, number] | undefined;
}

export default function ValueSlider({
  label,
  value,
  onChange,
  sliderValueToFilterValue,
  filterValueToSliderValue,
  max,
  prefix,
  minPlaceholder = "0",
  maxPlaceholder,
}: ValueSliderProps) {
  const switchIfInvertedValues = () => {
    if (value && value[0] >= 0 && value[1] >= 0 && value[0] > value[1]) {
      onChange([value[1], value[0]]);
    }
  };

  const defaultMaxPlaceholder =
    maxPlaceholder ?? `${prefix ?? ""}${max.toLocaleString()}`;
  const minValue = value && value[0] !== -1 ? value[0] : undefined;
  const maxValue = value && value[1] !== -1 ? value[1] : undefined;

  const handleOnChange = (
    _event: Event,
    newValue: number[],
    activeThumb: number,
  ) => {
    if (activeThumb === 0) {
      onChange([sliderValueToFilterValue(newValue[0]), value ? value[1] : -1]);
    } else {
      onChange([value ? value[0] : -1, sliderValueToFilterValue(newValue[1])]);
    }
  };

  return (
    <div className="flex gap-4 items-center">
      <NumericFormat
        id="min-number"
        size="small"
        prefix={prefix}
        placeholder={prefix ? `${prefix}${minPlaceholder}` : minPlaceholder}
        value={minValue}
        onValueChange={(values) =>
          onChange([values.floatValue ?? -1, value ? value[1] : -1])
        }
        allowNegative={false}
        customInput={TextField}
        thousandSeparator
        valueIsNumericString
        onBlur={switchIfInvertedValues}
      />
      <Slider
        aria-label={label}
        value={
          value
            ? [
                filterValueToSliderValue(0, value[0]),
                filterValueToSliderValue(100, value[1]),
              ]
            : [0, 100]
        }
        valueLabelDisplay="auto"
        getAriaValueText={(value) => value.toLocaleString()}
        valueLabelFormat={(value) => value.toLocaleString()}
        scale={sliderValueToFilterValue}
        max={filterValueToSliderValue(100, max)}
        onChange={handleOnChange}
      />
      <NumericFormat
        id="max-number"
        size="small"
        prefix={prefix}
        placeholder={defaultMaxPlaceholder}
        value={maxValue}
        onValueChange={(values) =>
          onChange([value ? value[0] : -1, values.floatValue ?? -1])
        }
        allowNegative={false}
        customInput={TextField}
        thousandSeparator
        valueIsNumericString
        onBlur={switchIfInvertedValues}
      />
    </div>
  );
}
